// The guest web telemetry allowlist, schema v1. It must match
// test/fixtures/guest-telemetry-v1.json, which telemetrySchema.test.mjs checks, and
// the server's copy in functions/src/guestTelemetry.ts. Anything not listed here is never sent.
const LATENCY = ["under_1s", "1s_3s", "3s_10s", "10s_plus"];

export const GUEST_TELEMETRY_SCHEMA = {
  version: 1,
  events: {
    page_load: {
      anonymous: true,
      properties: {
        route: { enum: ["invite", "feed", "entry", "media", "other"] },
        nav: { enum: ["navigate", "reload", "back_forward", "other"] },
        load_ms: { enum: LATENCY },
        browser: { enum: ["safari", "chrome", "firefox", "edge", "samsung", "in_app", "other"] },
        os: { enum: ["ios", "ipados", "android", "macos", "windows", "linux", "chromeos", "other"] },
        viewport: { enum: ["xs", "s", "m", "l", "xl"] },
      },
    },
    feed_loaded: {
      anonymous: false,
      properties: {
        result: { enum: ["ok", "empty", "session_ended", "error"] },
        time_to_feed: { enum: LATENCY },
        entries: { enum: ["0", "1_5", "6_25", "26_plus"] },
      },
    },
    entry_opened: {
      anonymous: false,
      properties: {
        result: { enum: ["ok", "error", "session_ended"] },
        source: { enum: ["feed", "direct"] },
      },
    },
    media_viewed: {
      anonymous: false,
      properties: {
        kind: { enum: ["photo", "video", "audio", "document"] },
        outcome: { enum: ["ok", "error", "too_large", "unavailable"] },
        media_surface: { enum: ["feed", "entry", "gallery"] },
        // Why a view failed (outcome error or unavailable); never a message. See media/failureReason.js.
        reason: {
          enum: ["key_denied", "key_unavailable", "session_lost", "fetch_failed", "range_unsupported", "integrity", "unsupported_format", "sw_timeout", "other"],
          optional: true,
        },
        // Video and audio that played: from the first play event to the first playing event.
        latency_bucket: { enum: LATENCY, optional: true },
        count: { type: "count" },
      },
    },
    video_playback: {
      anonymous: false,
      properties: {
        kind: { enum: ["video", "audio"] },
        phase: { enum: ["start", "failure", "stall"] },
        error_class: { enum: ["none", "aborted", "network", "decode", "src_not_supported", "unknown"] },
        count: { type: "count" },
      },
    },
    document_too_large: {
      anonymous: false,
      properties: {
        kind: { enum: ["document", "video", "audio"] },
      },
    },
    session_ended: {
      anonymous: true,
      properties: {
        reason: { enum: ["expired", "revoked", "invalid", "missing", "forgotten", "other"] },
        where: { enum: ["feed", "entry", "interaction", "media", "invite"] },
      },
    },
    interaction: {
      anonymous: false,
      properties: {
        kind: { enum: ["comment", "reaction"] },
        result: { enum: ["ok", "error"] },
        count: { type: "count" },
      },
    },
    js_error: {
      anonymous: true,
      properties: {
        class: { enum: ["Error", "TypeError", "ReferenceError", "SyntaxError", "RangeError", "unhandled_rejection", "resource", "other"] },
        fingerprint: { type: "fingerprint" },
        source: { enum: ["page", "service_worker"], optional: true },
        count: { type: "count" },
      },
    },
    media_no_service_worker: {
      anonymous: true,
      properties: {
        reason: { enum: ["unsupported", "registration_failed", "uncontrolled_after_reload"] },
      },
    },
  },
};

const MAX_STRING_LENGTH = 32;
const MAX_COUNT = 1000;
const FINGERPRINT = /^[0-9a-f]{16}$/;

/**
 * The event's declared properties, each valid, or null. Unknown properties are
 * dropped. `omitCount` leaves out `count`, which the client sets when it aggregates.
 * An optional property that is absent or undefined is left out; any other value must be valid.
 */
export function sanitizeProperties(name, properties, { omitCount = false } = {}) {
  if (!Object.hasOwn(GUEST_TELEMETRY_SCHEMA.events, name) || !isRecord(properties)) return null;
  const sanitized = {};
  for (const [key, rule] of Object.entries(GUEST_TELEMETRY_SCHEMA.events[name].properties)) {
    if (omitCount && key === "count") continue;
    if (rule.optional && (!Object.hasOwn(properties, key) || properties[key] === undefined)) continue;
    if (!Object.hasOwn(properties, key) || !isValidValue(rule, properties[key])) return null;
    sanitized[key] = properties[key];
  }
  return sanitized;
}

function isValidValue(rule, value) {
  if (typeof value === "string" && value.length > MAX_STRING_LENGTH) return false;
  if ("enum" in rule) return typeof value === "string" && rule.enum.includes(value);
  if (rule.type === "count") return Number.isSafeInteger(value) && value >= 1 && value <= MAX_COUNT;
  return typeof value === "string" && FINGERPRINT.test(value);
}

function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
